import { Workbook } from "exceljs";
import type { WalletTransactionRow } from "./wallet-api";
import type { CreditTransactionRow } from "./credit-api";
import type { OrderRow } from "./order-history-api";
import { getOrderServiceName, getOrderPlanName } from "./order-history-api";
import type { BBPSOrderRow } from "./bbps-orders-api";
import type { SharedSubOrderRow } from "./shared-sub-orders-api";

type ExcelRow = Record<string, string | number>;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatType(type: string): string {
  return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function downloadWorkbook(workbook: Workbook, filename: string) {
  workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  });
}

function exportDataToExcel(rows: ExcelRow[], sheetName: string, filename: string) {
  const workbook = new Workbook();
  addSheet(workbook, rows, sheetName);
  downloadWorkbook(workbook, filename);
}

/** Add a named worksheet to an already-created workbook. Extracted so
 *  multi-sheet exports (e.g. Summary + Transactions) can share the same
 *  auto-width logic without pulling the download-to-blob step in twice. */
function addSheet(workbook: Workbook, rows: ExcelRow[], sheetName: string) {
  const worksheet = workbook.addWorksheet(sheetName);
  const headers = Object.keys(rows[0] || {});
  const columnWidths = headers.map((key) => {
    const maxLen = Math.max(
      key.length,
      ...rows.map((row) => String(row[key] ?? "").length),
    );
    return { header: key, key, width: Math.min(maxLen + 2, 50) };
  });
  worksheet.columns = columnWidths;
  worksheet.addRows(rows);
}

export function exportTransactionsToExcel(
  rows: WalletTransactionRow[],
  filename = "transactions"
) {
  const data = rows.map((row) => ({
    "Transaction ID": row.id,
    "Amount (₹)": row.amount / 100,
    Type: formatType(row.type),
    Purpose: row.purpose,
    "Created At": formatDate(row.created_at),
    "Updated At": formatDate(row.updated_at),
  }));

  exportDataToExcel(data, "Transactions", filename);
}

/** Wallet-specific export — same underlying rows as exportTransactionsToExcel,
 *  but ships an Overview sheet + a By Type breakdown alongside the detail
 *  sheet so a downloaded file is self-explanatory ("how much of what did
 *  I spend / earn"). Metrics compute off the SAME rows that go into the
 *  Transactions sheet — filters and totals always reconcile. */
export function exportWalletTransactionsToExcel(
  rows: WalletTransactionRow[],
  filename = "wallet-transactions",
) {
  const workbook = new Workbook();

  // ── Aggregate pass ────────────────────────────────────────────────────
  let totalCreditsPaise = 0;   // sum of positive amounts (money in)
  let totalDebitsPaise = 0;    // sum of |negative amounts| (money out)
  const byType = new Map<string, { count: number; creditsPaise: number; debitsPaise: number }>();
  let minTs: number | null = null;
  let maxTs: number | null = null;

  for (const r of rows) {
    const amt = r.amount;
    if (amt >= 0) totalCreditsPaise += amt;
    else totalDebitsPaise += -amt;

    const bucket = byType.get(r.type) ?? { count: 0, creditsPaise: 0, debitsPaise: 0 };
    bucket.count += 1;
    if (amt >= 0) bucket.creditsPaise += amt;
    else bucket.debitsPaise += -amt;
    byType.set(r.type, bucket);

    const ts = new Date(r.created_at).getTime();
    if (Number.isFinite(ts)) {
      if (minTs === null || ts < minTs) minTs = ts;
      if (maxTs === null || ts > maxTs) maxTs = ts;
    }
  }

  const dateRange = minTs && maxTs
    ? `${formatDate(new Date(minTs).toISOString())} → ${formatDate(new Date(maxTs).toISOString())}`
    : "—";

  // ── Sheet 1: Overview ─────────────────────────────────────────────────
  const overview: ExcelRow[] = [
    { Metric: "Report Generated", Value: formatDate(new Date().toISOString()) },
    { Metric: "Date Range", Value: dateRange },
    { Metric: "Total Transactions", Value: rows.length },
    { Metric: "Total Credits (₹)", Value: totalCreditsPaise / 100 },
    { Metric: "Total Debits (₹)", Value: totalDebitsPaise / 100 },
    { Metric: "Net Balance Change (₹)", Value: (totalCreditsPaise - totalDebitsPaise) / 100 },
    { Metric: "Unique Transaction Types", Value: byType.size },
  ];
  addSheet(workbook, overview, "Overview");

  // ── Sheet 2: By Type ──────────────────────────────────────────────────
  // Sorted by absolute volume (credits + debits) so the biggest movers are
  // at the top — most useful for a "what am I spending on" scan.
  const byTypeRows: ExcelRow[] = [...byType.entries()]
    .map(([type, b]) => ({
      Type: formatType(type),
      Count: b.count,
      "Credits (₹)": b.creditsPaise / 100,
      "Debits (₹)": b.debitsPaise / 100,
      "Net (₹)": (b.creditsPaise - b.debitsPaise) / 100,
    }))
    .sort((a, b) => {
      const av = Number(a["Credits (₹)"]) + Number(a["Debits (₹)"]);
      const bv = Number(b["Credits (₹)"]) + Number(b["Debits (₹)"]);
      return bv - av;
    });
  addSheet(workbook, byTypeRows, "By Type");

  // ── Sheet 3: Transactions (one row per txn) ───────────────────────────
  const data = rows.map((row) => ({
    "Transaction ID": row.id,
    "Amount (₹)": row.amount / 100,
    Type: formatType(row.type),
    Purpose: row.purpose,
    "Created At": formatDate(row.created_at),
    "Updated At": formatDate(row.updated_at),
  }));
  addSheet(workbook, data, "Transactions");

  downloadWorkbook(workbook, filename);
}

/** Enriched customer-transaction row shape from getCustomerTransactions.
 *  Kept as a local interface (not imported from the page file) so this util
 *  stays free of cross-cutting page dependencies. */
export interface CustomerTransactionExportRow {
  id: string;
  amount: number;              // paise (signed — negative = debit)
  type: string;
  purpose: string;
  created_at: string;
  payment_id: string | null;
  auth: { fullname: string; phone: string };
  service_name: string | null;
  external_id: string | null;
  earnings_paise: number | null;
}

/** wallet types that count as an actual customer purchase — used for the
 *  Purchase Volume / Purchases stats on the Overview sheet. Kept aligned
 *  with the admin panel's rollup so numbers reconcile across dashboards. */
const PURCHASE_TYPES = new Set([
  "buy_debit",
  "b2b_buy_debit",
  "subscription_buy",
  "group_join_debit",
]);

export function exportCustomerTransactionsToExcel(
  rows: CustomerTransactionExportRow[],
  filename = "customer-transactions",
) {
  const workbook = new Workbook();

  // ── Sheet 1: Overview ─────────────────────────────────────────────────
  // Aggregate metrics computed off the SAME rows shipped in the second
  // sheet — so the two views always reconcile (no stale totals if the caller
  // has already applied filters).
  const totalRows = rows.length;
  let purchaseCount = 0;
  let purchaseVolumePaise = 0;         // sum of |amount| for purchase types
  let totalEarningsPaise = 0;
  const uniqueUsers = new Set<string>();
  const uniqueBrands = new Set<string>();
  let minTs: number | null = null;
  let maxTs: number | null = null;

  for (const r of rows) {
    if (PURCHASE_TYPES.has(r.type)) {
      purchaseCount += 1;
      purchaseVolumePaise += Math.abs(r.amount);
    }
    if (r.earnings_paise != null) totalEarningsPaise += r.earnings_paise;
    uniqueUsers.add(r.external_id ?? r.auth.phone);
    if (r.service_name) uniqueBrands.add(r.service_name);
    const ts = new Date(r.created_at).getTime();
    if (Number.isFinite(ts)) {
      if (minTs === null || ts < minTs) minTs = ts;
      if (maxTs === null || ts > maxTs) maxTs = ts;
    }
  }

  const dateRange = minTs && maxTs
    ? `${formatDate(new Date(minTs).toISOString())} → ${formatDate(new Date(maxTs).toISOString())}`
    : "—";

  const overview: ExcelRow[] = [
    { Metric: "Report Generated", Value: formatDate(new Date().toISOString()) },
    { Metric: "Date Range", Value: dateRange },
    { Metric: "Total Transactions", Value: totalRows },
    { Metric: "Total Purchases", Value: purchaseCount },
    { Metric: "Total Purchase Volume (₹)", Value: purchaseVolumePaise / 100 },
    { Metric: "Total Earnings (₹)", Value: totalEarningsPaise / 100 },
    { Metric: "Unique Customers", Value: uniqueUsers.size },
    { Metric: "Unique Brands", Value: uniqueBrands.size },
  ];

  addSheet(workbook, overview, "Overview");

  // ── Sheet 2: Transactions (one row per txn) ───────────────────────────
  // Payment ID / Transaction ID intentionally omitted — noise for the client;
  // internal reconciliation happens via the admin panel.
  const data = rows.map((row) => ({
    "Date": formatDate(row.created_at),
    "User": row.auth.fullname,
    "User ID": row.external_id ?? row.auth.phone,
    "Internal Phone": row.auth.phone,
    "Brand": row.service_name ?? "",
    "Type": formatType(row.type),
    "Purpose": row.purpose,
    "Amount (₹)": row.amount / 100,           // signed — negative = debit
    "Earnings (₹)": row.earnings_paise != null ? row.earnings_paise / 100 : "",
  }));

  addSheet(workbook, data, "Transactions");
  downloadWorkbook(workbook, filename);
}

export function exportCreditTransactionsToExcel(
  rows: CreditTransactionRow[],
  filename = "credit-transactions"
) {
  const data = rows.map((row) => ({
    "Transaction ID": row.id,
    Credit: row.credit,
    Type: formatType(row.type),
    Purpose: row.purpose,
    "Payment ID": row.payment_id ?? "",
    From: row.from ?? "",
    "Created At": formatDate(row.created_at),
    "Updated At": formatDate(row.updated_at),
  }));

  exportDataToExcel(data, "Credit Transactions", filename);
}

export function exportOrdersToExcel(
  rows: OrderRow[],
  filename = "gift-card-orders"
) {
  const data = rows.map((row) => ({
    "Order ID": row.id,
    Service: getOrderServiceName(row),
    Plan: getOrderPlanName(row),
    Coupon: row.coupon,
    PIN: row.pin ?? "",
    "Face Value (₹)": row.amount,
    "Paid (₹)": row.discounted_amount,
    "Savings (₹)": +(row.amount - row.discounted_amount).toFixed(2),
    Status: row.delivery_status,
    "Allocated At": row.allocated_at ? formatDate(row.allocated_at) : "",
    Expires: row.expiring_at ?? "",
    "Created At": formatDate(row.created_at),
  }));

  exportDataToExcel(data, "Gift Card Orders", filename);
}

export function exportBBPSOrdersToExcel(
  rows: BBPSOrderRow[],
  filename = "bbps-orders"
) {
  const data = rows.map((row) => ({
    "Order ID": row.order_id,
    Operator: row.whatsub_bbps_operator?.name ?? row.operator,
    "CA Number": row.canumber,
    "Amount (₹)": Number(row.amount),
    Status: row.status,
    "Created At": formatDate(row.created_at),
    "Updated At": formatDate(row.updated_at),
  }));

  exportDataToExcel(data, "BBPS Orders", filename);
}

export function exportSharedSubOrdersToExcel(
  rows: SharedSubOrderRow[],
  filename = "shared-subscription-orders"
) {
  const data = rows.map((row) => ({
    "Order ID": row.short_id,
    ID: row.id,
    Service: row.whatsub_plan?.whatsub_service?.service_name ?? "—",
    Plan: row.whatsub_plan?.plan_name ?? "—",
    "Price (₹)": row.price,
    Status: row.status,
    "Payment ID": row.payment_id ?? "",
    "Allocated At": row.allocated_at ? formatDate(row.allocated_at) : "",
    "Created At": formatDate(row.created_at),
  }));

  exportDataToExcel(data, "Shared Sub Orders", filename);
}
